export type PointId = string;
export type LocationInput = { lat: number; lon: number; name: string };

export const MODELS = ["icon_d2", "icon_eu", "ecmwf_ifs025"] as const;
export type ModelId = (typeof MODELS)[number];

export const MODEL_META: Record<ModelId, { label: string; resolution: string; provider: string; hi15: boolean }> = {
  icon_d2: { label: "ICON-D2", resolution: "2.2 km", provider: "DWD (Niemcy)", hi15: true },
  icon_eu: { label: "ICON-EU", resolution: "7 km", provider: "DWD (Niemcy)", hi15: false },
  ecmwf_ifs025: { label: "ECMWF IFS", resolution: "25 km", provider: "ECMWF (Europa)", hi15: false },
};

export type StormSample = {
  isValid: boolean;
  trend: "rosnie" | "spada" | "stabilnie";
  time: string;
  offsetMin: number;
  cape: number | null;
  cin: number | null;
  li: number | null;
  precip: number | null;
  gust: number | null;
  windSpeed: number | null;
  windDir: number | null;
  wind500: number | null;
  rh850: number | null;
  lightning: number | null;
  dewPoint: number | null;
  freezingLevel: number | null;
  pblHeight: number | null;
  temp850: number | null;
  temp500: number | null;
  lapseRate: number | null;
  risk: number;
  riskBreakdown: {
    capeS: number;
    liS: number;
    shearS: number;
    precipS: number;
    lightningS: number;
    rhS: number;
    cinPenalty: number;
  };
  threats: {
    hail: number;
    tornado: number;
    flood: number;
  };
};

export type PointForecast = {
  pointId: PointId;
  name: string;
  lat: number;
  lon: number;
  models: Partial<Record<ModelId, StormSample[]>>;
};

export type StormForecastResponse = {
  generatedAt: string;
  points: PointForecast[];
  ensemble: {
    horizonMin: number;
    risk: number;
    label: "spokojnie" | "wzrost niestabilności" | "ryzyko burzy" | "wysokie ryzyko" | "ekstremalne";
    perModel: { model: ModelId; risk: number; sample: StormSample | null }[];
  }[];
  trajectory: {
    fromDir: number | null;
    advectionLabel: string;
  };
  currentObs: any;
};

const OM_HOURLY = [
  "cape",
  "convective_inhibition",
  "lifted_index",
  "precipitation",
  "wind_gusts_10m",
  "wind_speed_10m",
  "wind_direction_10m",
  "wind_speed_500hPa",
  "wind_direction_500hPa",
  "relative_humidity_850hPa",
  "dew_point_2m",
  "freezing_level_height",
  "boundary_layer_height",
  "temperature_850hPa",
  "temperature_500hPa",
].join(",");

const OM_MIN15 = ["cape", "lightning_potential", "precipitation", "wind_gusts_10m"].join(",");

function clamp01(x: number) {
  return Math.max(0, Math.min(1, x));
}

function computeRisk(s: Omit<StormSample, "risk" | "riskBreakdown" | "threats" | "lapseRate" | "isValid">) {
  const isValid = s.cape !== null || s.dewPoint !== null || s.temp850 !== null;
  if (!isValid) {
    return {
      isValid: false,
      lapseRate: null,
      risk: 0,
      riskBreakdown: { capeS: 0, liS: 0, shearS: 0, precipS: 0, lightningS: 0, rhS: 0, cinPenalty: 0 },
      threats: { hail: 0, tornado: 0, flood: 0 },
    };
  }

  const capeS = s.cape != null ? clamp01(s.cape / 2000) : 0;
  const liS = s.li != null ? clamp01((0 - s.li) / 8) : 0;
  const dewS = s.dewPoint != null ? clamp01((s.dewPoint - 14) / 6) : 0;
  const cinPenalty = s.cin != null ? clamp01(Math.abs(s.cin) / 100) : 0;

  let lapseRate: number | null = null;
  if (s.temp850 != null && s.temp500 != null) {
    lapseRate = (s.temp850 - s.temp500) / 4;
  }
  const lapseS = lapseRate != null ? clamp01((lapseRate - 5.5) / 2.5) : 0;

  const thermoScore = (capeS * 0.35 + liS * 0.25 + dewS * 0.25 + lapseS * 0.15) * (1 - 0.8 * cinPenalty);

  const precipS = s.precip != null ? clamp01(s.precip / 8) : 0;
  const lightningS = s.lightning != null ? clamp01(s.lightning / 2) : 0;
  const gustS = s.gust != null ? clamp01((s.gust - 15) / 10) : 0;

  let activeScore = 0;
  if (s.lightning != null) {
    activeScore = precipS * 0.4 + lightningS * 0.4 + gustS * 0.2;
  } else {
    activeScore = precipS * 0.6 + gustS * 0.4;
    // KOREKTA: Jeśli nie ma piorunów i brakuje energii termodynamicznej (CAPE < 150),
    // drastycznie tniemy wpływ samego opadu, żeby zwykły deszcz nie udawał burzy.
    if ((s.cape ?? 0) < 150) {
      activeScore *= 0.3;
    }
  }

  const shear = s.wind500 != null && s.windSpeed != null ? Math.abs(s.wind500 - s.windSpeed) : 0;
  const shearS = clamp01(shear / 20);

  let finalRisk = Math.max(thermoScore, activeScore);
  finalRisk = clamp01(finalRisk + shearS * 0.15);
  const risk = Math.round(finalRisk * 100);

  let hail = 0;
  if (s.freezingLevel != null && finalRisk > 0.3) {
    const levelPenalty = clamp01(Math.abs(s.freezingLevel - 3000) / 2000);
    hail = Math.round(clamp01(capeS * 0.4 + lapseS * 0.3 + (1 - levelPenalty) * 0.3) * risk);
  }

  let tornado = 0;
  if (s.pblHeight != null && finalRisk > 0.3) {
    const pblScore = clamp01((1500 - s.pblHeight) / 1000);
    tornado = Math.round(clamp01(shearS * 0.6 + pblScore * 0.4) * risk);
  }

  let flood = 0;
  if (s.windSpeed != null && s.wind500 != null && finalRisk > 0.2) {
    const stormMotion = (s.windSpeed + s.wind500) / 2;
    const slowMotionScore = clamp01((15 - stormMotion) / 15);
    flood = Math.round(clamp01(precipS * 0.5 + dewS * 0.3 + slowMotionScore * 0.2) * risk);
  }

  return {
    isValid: true,
    lapseRate,
    risk,
    riskBreakdown: { capeS, liS, shearS, precipS, lightningS, rhS: dewS, cinPenalty },
    threats: { hail, tornado, flood },
  };
}

function labelForRisk(r: number): StormForecastResponse["ensemble"][number]["label"] {
  if (r < 15) return "spokojnie";
  if (r < 35) return "wzrost niestabilności";
  if (r < 60) return "ryzyko burzy";
  if (r < 80) return "wysokie ryzyko";
  return "ekstremalne";
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function kmhToMs(value: unknown): number | null {
  const n = asNumber(value);
  return n == null ? null : n / 3.6;
}

function modelKey(container: Record<string, unknown> | undefined, key: string, model: ModelId) {
  const suffixed = `${key}_${model}`;
  return container && suffixed in container ? suffixed : key;
}

function valueAtNearestHour(
  j: any,
  key: string,
  model: ModelId,
  targetMs: number,
  convert: (value: unknown) => number | null = asNumber,
) {
  const times: string[] | undefined = j.hourly?.time;
  const values: unknown[] | undefined = j.hourly?.[modelKey(j.hourly, key, model)];
  if (!times?.length || !values?.length) return null;

  let bestIndex = -1;
  let bestDiff = Infinity;
  for (let i = 0; i < times.length; i++) {
    const diff = Math.abs(new Date(times[i] + "Z").getTime() - targetMs);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIndex = i;
    }
  }
  if (bestIndex < 0 || bestDiff > 75 * 60_000) return null;
  return convert(values[bestIndex]);
}

function parseSamples(j: any, model: ModelId): StormSample[] {
  const now = Date.now();
  const samples: StormSample[] = [];

  if (MODEL_META[model].hi15 && j.minutely_15?.time) {
    const t: string[] = j.minutely_15.time;
    const minCape = modelKey(j.minutely_15, "cape", model);
    const minLightning = modelKey(j.minutely_15, "lightning_potential", model);
    const minPrecip = modelKey(j.minutely_15, "precipitation", model);
    const minGust = modelKey(j.minutely_15, "wind_gusts_10m", model);

    for (let i = 0; i < t.length; i++) {
      const ts = new Date(t[i] + "Z").getTime();
      const off = Math.round((ts - now) / 60000);
      if (off < -15 || off > 180) continue;

      const base = {
        trend: "stabilnie" as const,
        time: t[i] + "Z",
        offsetMin: off,
        cape: asNumber(j.minutely_15[minCape]?.[i]) ?? valueAtNearestHour(j, "cape", model, ts),
        cin: valueAtNearestHour(j, "convective_inhibition", model, ts),
        li: valueAtNearestHour(j, "lifted_index", model, ts),
        precip: asNumber(j.minutely_15[minPrecip]?.[i]) ?? valueAtNearestHour(j, "precipitation", model, ts),
        gust: kmhToMs(j.minutely_15[minGust]?.[i]) ?? valueAtNearestHour(j, "wind_gusts_10m", model, ts, kmhToMs),
        windSpeed: valueAtNearestHour(j, "wind_speed_10m", model, ts, kmhToMs),
        windDir: valueAtNearestHour(j, "wind_direction_10m", model, ts),
        wind500: valueAtNearestHour(j, "wind_speed_500hPa", model, ts, kmhToMs),
        rh850: valueAtNearestHour(j, "relative_humidity_850hPa", model, ts),
        lightning: asNumber(j.minutely_15[minLightning]?.[i]),
        dewPoint: valueAtNearestHour(j, "dew_point_2m", model, ts),
        freezingLevel: valueAtNearestHour(j, "freezing_level_height", model, ts),
        pblHeight: valueAtNearestHour(j, "boundary_layer_height", model, ts),
        temp850: valueAtNearestHour(j, "temperature_850hPa", model, ts),
        temp500: valueAtNearestHour(j, "temperature_500hPa", model, ts),
      };
      samples.push({ ...base, ...computeRisk(base) });
    }
  }

  if (j.hourly?.time) {
    const t: string[] = j.hourly.time;
    for (let i = 0; i < t.length; i++) {
      const ts = new Date(t[i] + "Z").getTime();
      const off = Math.round((ts - now) / 60000);
      if (off < -60 || off > 24 * 60) continue;
      if (MODEL_META[model].hi15 && off >= 0 && off <= 180) continue;

      const base = {
        trend: "stabilnie" as const,
        time: t[i] + "Z",
        offsetMin: off,
        cape: asNumber(j.hourly[modelKey(j.hourly, "cape", model)]?.[i]),
        cin: asNumber(j.hourly[modelKey(j.hourly, "convective_inhibition", model)]?.[i]),
        li: asNumber(j.hourly[modelKey(j.hourly, "lifted_index", model)]?.[i]),
        precip: asNumber(j.hourly[modelKey(j.hourly, "precipitation", model)]?.[i]),
        gust: kmhToMs(j.hourly[modelKey(j.hourly, "wind_gusts_10m", model)]?.[i]),
        windSpeed: kmhToMs(j.hourly[modelKey(j.hourly, "wind_speed_10m", model)]?.[i]),
        windDir: asNumber(j.hourly[modelKey(j.hourly, "wind_direction_10m", model)]?.[i]),
        wind500: kmhToMs(j.hourly[modelKey(j.hourly, "wind_speed_500hPa", model)]?.[i]),
        rh850: asNumber(j.hourly[modelKey(j.hourly, "relative_humidity_850hPa", model)]?.[i]),
        lightning: null,
        dewPoint: asNumber(j.hourly[modelKey(j.hourly, "dew_point_2m", model)]?.[i]),
        freezingLevel: asNumber(j.hourly[modelKey(j.hourly, "freezing_level_height", model)]?.[i]),
        pblHeight: asNumber(j.hourly[modelKey(j.hourly, "boundary_layer_height", model)]?.[i]),
        temp850: asNumber(j.hourly[modelKey(j.hourly, "temperature_850hPa", model)]?.[i]),
        temp500: asNumber(j.hourly[modelKey(j.hourly, "temperature_500hPa", model)]?.[i]),
      };
      samples.push({ ...base, ...computeRisk(base) });
    }
  }

  samples.sort((a, b) => a.offsetMin - b.offsetMin);
  for (let i = 1; i < samples.length; i++) {
    const diff = samples[i].risk - samples[i - 1].risk;
    if (diff >= 10) samples[i].trend = "rosnie";
    else if (diff <= -10) samples[i].trend = "spada";
  }
  return samples;
}

function lerpNullable(a: number | null, b: number | null, t: number) {
  if (a == null && b == null) return null;
  if (a == null) return b;
  if (b == null) return a;
  return a + (b - a) * t;
}

function interpolateSamples(a: StormSample, b: StormSample, targetMin: number): StormSample {
  const span = b.offsetMin - a.offsetMin;
  const t = span === 0 ? 0 : clamp01((targetMin - a.offsetMin) / span);
  const base = {
    time: new Date(Date.now() + targetMin * 60_000).toISOString(),
    offsetMin: targetMin,
    cape: lerpNullable(a.cape, b.cape, t),
    cin: lerpNullable(a.cin, b.cin, t),
    li: lerpNullable(a.li, b.li, t),
    precip: lerpNullable(a.precip, b.precip, t),
    gust: lerpNullable(a.gust, b.gust, t),
    windSpeed: lerpNullable(a.windSpeed, b.windSpeed, t),
    windDir: lerpNullable(a.windDir, b.windDir, t),
    wind500: lerpNullable(a.wind500, b.wind500, t),
    rh850: lerpNullable(a.rh850, b.rh850, t),
    lightning: lerpNullable(a.lightning, b.lightning, t),
    dewPoint: lerpNullable(a.dewPoint, b.dewPoint, t),
    freezingLevel: lerpNullable(a.freezingLevel, b.freezingLevel, t),
    pblHeight: lerpNullable(a.pblHeight, b.pblHeight, t),
    temp850: lerpNullable(a.temp850, b.temp850, t),
    temp500: lerpNullable(a.temp500, b.temp500, t),
    trend: a.trend,
  };
  return { ...base, ...computeRisk(base) };
}

function pickAt(samples: StormSample[], targetMin: number, toleranceMin: number): StormSample | null {
  if (!samples.length) return null;
  const before = [...samples].reverse().find((s) => s.offsetMin <= targetMin);
  const after = samples.find((s) => s.offsetMin >= targetMin);
  if (before && after && before !== after && targetMin - before.offsetMin <= 75 && after.offsetMin - targetMin <= 75) {
    return interpolateSamples(before, after, targetMin);
  }
  let best: StormSample | null = null;
  let bestDiff = Infinity;
  for (const s of samples) {
    const d = Math.abs(s.offsetMin - targetMin);
    if (d < bestDiff && d <= toleranceMin) {
      best = s;
      bestDiff = d;
    }
  }
  return best;
}

async function fetchAllModelsAllPoints(
  points: { id: string; name: string; lat: number; lon: number }[],
): Promise<{ modelsData: Record<ModelId, Record<PointId, StormSample[]>>; currentObs: any }> {
  const out = {} as Record<ModelId, Record<PointId, StormSample[]>>;
  const currentObsList: any[] = [];
  const skippedModels: string[] = [];

  for (const model of MODELS) {
    out[model] = {} as Record<PointId, StormSample[]>;

    let baseUrl = "https://api.open-meteo.com/v1/forecast";
    if (model === "icon_d2" || model === "icon_eu") {
      baseUrl = "https://api.open-meteo.com/v1/dwd-icon";
    }

    const url = new URL(baseUrl);
    url.searchParams.set("latitude", points.map((p) => p.lat.toFixed(4)).join(","));
    url.searchParams.set("longitude", points.map((p) => p.lon.toFixed(4)).join(","));
    url.searchParams.set("models", model);
    url.searchParams.set("hourly", OM_HOURLY);
    if (MODEL_META[model].hi15) {
      url.searchParams.set("minutely_15", OM_MIN15);
    }
    url.searchParams.set("forecast_days", "2");
    url.searchParams.set("timezone", "UTC");
    url.searchParams.set(
      "current",
      "temperature_2m,relative_humidity_2m,precipitation,wind_speed_10m,wind_direction_10m,wind_gusts_10m,weather_code",
    );

    for (let attempt = 0; attempt < 2; attempt++) {
      let r: Response;
      try {
        r = await fetch(url.toString());
      } catch (err) {
        await new Promise((res) => setTimeout(res, 800));
        continue;
      }
      if (r.ok) {
        const j: any = await r.json();
        const arr: any[] = Array.isArray(j) ? j : [j];
        points.forEach((_, idx) => {
          if (arr[idx]?.current) {
            currentObsList.push(arr[idx].current);
          }
        });
        points.forEach((p, idx) => {
          out[model][p.id] = arr[idx] ? parseSamples(arr[idx], model) : [];
        });
        break;
      }
      if (r.status === 400) break;
      if (r.status === 429) {
        skippedModels.push(MODEL_META[model].label);
        break;
      }
      await new Promise((res) => setTimeout(res, 1000));
    }

    await new Promise((res) => setTimeout(res, 600));
  }

  const hasAnyData = MODELS.some((m) => Object.keys(out[m]).length > 0);
  if (!hasAnyData) {
    throw new Error(
      `Przekroczono darmowy limit API Open-Meteo dla Twojego IP. Modele (${skippedModels.join(", ")}) zablokowane.`,
    );
  }

  // --- AGREGACJA DANYCH NA ŻYWO (Zabijamy komórkę "Frankensteina") ---
  let mergedCurrentObs: any = null;
  if (currentObsList.length > 0) {
    mergedCurrentObs = currentObsList.reduce((acc, curr) => {
      // Zamiast miksować parametry, oceniamy powagę CAŁEJ obserwacji jako jednego, fizycznego punktu
      const scoreObs = (obs: any) => {
        let score = 0;
        const code = obs.weather_code || 0;
        const precip = obs.precipitation || 0;

        // Twardy filtr - kasujemy halucynacje burzowe bez opadu
        if (precip < 0.5 && [95, 96, 99].includes(code)) {
          // nie dodajemy punktów za fałszywy kod
        } else if ([95, 96, 99].includes(code)) {
          score += 100;
        } else if ([65, 67, 81, 82].includes(code)) {
          score += 50;
        }

        score += precip * 10;
        score += obs.wind_gusts_10m || 0;
        return score;
      };

      // Zwracamy w CAŁOŚCI ten odczyt z 15 punktów, który zebrał najwięcej punktów grozy
      return scoreObs(curr) > scoreObs(acc) ? curr : acc;
    });
  }

  return { modelsData: out, currentObs: mergedCurrentObs };
}
// --- KASKADOWY AGREGATOR DANYCH RZECZYWISTYCH (GROUND TRUTH) ---
// Pobiera dane z profesjonalnych API (jeśli podasz klucze).
// Jeśli wyczerpiesz limity, płynnie spada na darmowe Open-Meteo.
async function fetchRealTimeGroundTruth(lat: number, lon: number, openMeteoFallback: any) {
  // UWAGA: Tutaj wklej swoje darmowe klucze, jeśli chcesz odblokować potęgę tych API!
  const OWM_API_KEY = ""; // Pobierz z: openweathermap.org
  const TOMORROW_API_KEY = ""; // Pobierz z: tomorrow.io

  // 1. PRÓBA: Tomorrow.io (Najbardziej precyzyjny Real-Time Radar)
  if (TOMORROW_API_KEY) {
    try {
      const res = await fetch(
        `https://api.tomorrow.io/v4/weather/realtime?location=${lat},${lon}&apikey=${TOMORROW_API_KEY}`,
      );
      if (res.ok) {
        const data = await res.json();
        const vals = data.data.values;
        return {
          temperature_2m: vals.temperature,
          relative_humidity_2m: vals.humidity,
          precipitation: vals.precipitationIntensity,
          wind_speed_10m: vals.windSpeed * 3.6, // m/s na km/h
          wind_gusts_10m: vals.windGust * 3.6,
          weather_code: vals.weatherCode,
          source: "Tomorrow.io (Radar Live)",
        };
      }
    } catch (e) {
      console.warn("Tomorrow.io padło, spadamy niżej...");
    }
  }

  // 2. PRÓBA: OpenWeatherMap (Bardzo gęsta sieć stacji naziemnych)
  if (OWM_API_KEY) {
    try {
      const res = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&appid=${OWM_API_KEY}&units=metric`,
      );
      if (res.ok) {
        const data = await res.json();
        // Konwersja kodów OWM na kody WMO
        let wmoCode = 0;
        const id = data.weather[0]?.id || 0;
        if (id >= 200 && id < 300)
          wmoCode = 95; // Burza
        else if (id >= 500 && id < 600) wmoCode = id === 502 || id === 503 || id === 504 ? 65 : 61; // Deszcz

        return {
          temperature_2m: data.main.temp,
          relative_humidity_2m: data.main.humidity,
          precipitation: data.rain ? data.rain["1h"] || 0 : 0,
          wind_speed_10m: data.wind.speed * 3.6,
          wind_gusts_10m: (data.wind.gust || data.wind.speed) * 3.6,
          weather_code: wmoCode,
          source: "OpenWeatherMap (Stacje SYNOP)",
        };
      }
    } catch (e) {
      console.warn("OWM padło, spadamy niżej...");
    }
  }

  // 3. FALLBACK: Open-Meteo (Nasz dotychczasowy miks najgorszych wskazań w promieniu 20km)
  return {
    ...openMeteoFallback,
    source: "Open-Meteo (Agregacja wielomodelowa)",
  };
}

// EKSPORT GŁÓWNY: Wywoływany z przeglądarki użytkownika
export async function getStormForecast(loc: LocationInput): Promise<StormForecastResponse> {
  const pointsList = [
    { id: "center", name: loc.name, lat: loc.lat, lon: loc.lon },
    { id: "north", name: "Północ", lat: loc.lat + 0.15, lon: loc.lon },
    { id: "south", name: "Południe", lat: loc.lat - 0.15, lon: loc.lon },
    { id: "east", name: "Wschód", lat: loc.lat, lon: loc.lon + 0.25 },
    { id: "west", name: "Zachód", lat: loc.lat, lon: loc.lon - 0.25 },
  ];

  const fetchResult = await fetchAllModelsAllPoints(pointsList);
  const allData = fetchResult?.modelsData ?? null;
  const openMeteoCurrentObs = fetchResult?.currentObs ?? null;

  // URUCHAMIAMY KASKADOWY SPADOCHRON DLA DANYCH NA ŻYWO
  const currentObs = await fetchRealTimeGroundTruth(loc.lat, loc.lon, openMeteoCurrentObs);

  const points: PointForecast[] = pointsList.map((p) => {
    const models: PointForecast["models"] = {};
    for (const model of MODELS) {
      if (allData?.[model]?.[p.id]?.length) models[model] = allData[model][p.id];
    }
    return { pointId: p.id, name: p.name, lat: p.lat, lon: p.lon, models };
  });

  const modelWeight: Record<ModelId, number> = { icon_d2: 0.5, icon_eu: 0.3, ecmwf_ifs025: 0.2 };
  const center = points.find((p) => p.pointId === "center")!;
  const horizons = [15, 30, 60, 120];

  const ensemble = horizons.map((h) => {
    const perModel = MODELS.map((m) => {
      const samples = center.models[m] ?? [];
      const tol = h <= 30 ? 12 : 35;
      const s = pickAt(samples, h, tol);
      return { model: m, risk: s?.risk ?? 0, sample: s };
    });
    let wsum = 0,
      w = 0;
    for (const pm of perModel) {
      if (pm.sample && pm.sample.isValid) {
        wsum += pm.risk * modelWeight[pm.model];
        w += modelWeight[pm.model];
      }
    }
    const risk = w > 0 ? Math.round(wsum / w) : 0;
    return { horizonMin: h, risk, label: labelForRisk(risk), perModel };
  });

  const iconEu = center.models.icon_eu ?? [];
  const nearHour = pickAt(iconEu, 60, 60);
  const fromDir = nearHour?.windDir ?? null;

  let strongest: { name: string; risk: number } | null = null;

  for (const p of points) {
    if (p.pointId === "center") continue;
    const m = p.models.icon_d2 ?? p.models.icon_eu ?? [];

    // Przeszukujemy całe okno 2 godzin dla punktu otaczającego, szukając najgorszego momentu
    let maxRiskInPoint = 0;
    for (const s of m) {
      if (s.offsetMin >= 0 && s.offsetMin <= 120 && s.isValid) {
        maxRiskInPoint = Math.max(maxRiskInPoint, s.risk);
      }
    }

    if (maxRiskInPoint > (strongest?.risk ?? 0)) {
      strongest = { name: p.name, risk: maxRiskInPoint };
    }
  }
  let advectionLabel = "Brak wyraźnego sygnału advekcji burz.";
  if (strongest && strongest.risk >= 30) {
    advectionLabel = `Najwyższe ryzyko: kierunek ${strongest.name} (${strongest.risk}/100). Jeśli wiatr w średniej troposferze utrzyma kierunek — układ może przejść nad wybranym punktem.`;
  }

  return {
    generatedAt: new Date().toISOString(),
    points,
    ensemble,
    trajectory: { fromDir, advectionLabel },
    currentObs,
  };
}
